import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSupplierPriceListQuery } from 'api';
import { Form, FormInstance, TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { PERMISSIONS } from 'constants/permissions';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { SupplierPriceListDataType } from 'feature/supplierTabs/priceList/index';

const initialFilters: Record<string, string | undefined> = {
  supplierId: undefined,
  createdDateFrom: undefined,
  createdDateTo: undefined,
  lastUpdatedDateFrom: undefined,
  lastUpdatedDateTo: undefined,
  expireDateFrom: undefined,
  expireDateTo: undefined,
};
type Align = `Pricelists` | `Online Shop`;
const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export interface IDeleteItem {
  id: string;
  supplierId: string;
  fileName: string;
}

export interface IBrand {
  id: string;
  name: string;
}

export const usePriceList = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [openFilters, setOpenFilters] = useState(false);
  const [deleteItem, setDeleteItem] = useState<IDeleteItem | undefined>(undefined);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openOnlineShopModal, setOpenOnlineShopModal] = useState(false);
  const [drawerState, setDrawerState] = useState<boolean>(false);
  const [brandsData, setBrandsData] = useState<IBrand[] | undefined>(undefined);
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [search, setSearch] = useState('');
  const [alignValue, setAlignValue] = React.useState<Align>('Pricelists');
  const [sort, setSort] = useState<SortParamsType>(initialSort);

  const [filtersForm] = Form.useForm<FormInstance>();
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasAccess } = useAccessControl();

  const { data: priceList, isLoading } = useSupplierPriceListQuery({
    supplierId: id,
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onSegmentedChange = useCallback((key: Align) => {
    setAlignValue(key);
  }, []);
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}`);
    },
    [navigate]
  );
  const onRedirectToBrand = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
    },
    [navigate]
  );

  const onExpand = useCallback((brands?: IBrand[]) => {
    setBrandsData(brands);
    setDrawerState((prevState) => !prevState);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.expireDate?.[0] && {
          expireDateFrom: dateFormatter(values.expireDate[0], 'start'),
        }),
        ...(values.expireDate?.[1] && {
          expireDateTo: dateFormatter(values.expireDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  const handleTableChange: TableProps<SupplierPriceListDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<SupplierPriceListDataType> | SorterResult<SupplierPriceListDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const priceListOptions = useMemo(() => {
    const options = [];
    if (hasAccess(PERMISSIONS.suppliers.price_list.read)) {
      options.push(`Pricelists`);
    }
    if (hasAccess(PERMISSIONS.suppliers.online_shop.read)) {
      options.push(`Online Shop`);
    }
    return options;
  }, [hasAccess]);

  const onHandleDelete = useCallback((item: IDeleteItem) => {
    setDeleteItem(item);
  }, []);

  const onDeleteModalClose = useCallback(() => {
    setDeleteItem(undefined);
  }, []);

  const onAddModalStateChange = useCallback(() => {
    setOpenAddModal((oState) => !oState);
  }, []);

  const onOpenOnlineShopModalStateChange = useCallback(() => {
    setOpenOnlineShopModal((oState) => !oState);
  }, []);

  return {
    routeChange,
    handleTableChange,
    priceList,
    pagination,
    isLoading,
    setSearch,
    onHandleDelete,
    onDeleteModalClose,
    deleteItem,
    openAddModal,
    openOnlineShopModal,
    onAddModalStateChange,
    onOpenOnlineShopModalStateChange,
    applyFilters,
    resetFilters,
    filtersForm,
    openFilters,
    onOpenFilters,
    onCloseFilters,
    priceListOptions,
    onSegmentedChange,
    alignValue,
    onRedirectToBrand,
    onExpand,
    drawerState,
    brandsData,
  };
};
